import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStreaming } from "../context/StreamingContext";
import "./Index.css";
import questionsData from "../data/questions.json";

const Index: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const [selectedNotes, setSelectedNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    setStreamedData,
    setParsedData,
    setClinicalCaseData,
    setIsStreaming,
    setQuestion,
    setClinicalNotes,
    setFollowUpQuestions,
    setIsLoadingFollowUp,
    setConversationMessages,
    setCurrentStreamingMessage,
    getAbortController,
  } = useStreaming();

  const handleSubmit = async () => {
    if (inputValue.trim()) {
      setIsLoading(true);
      setQuestion(inputValue);
      setClinicalNotes(selectedNotes);
      setIsStreaming(true);
      setStreamedData("");
      setParsedData(null);
      setClinicalCaseData(null);
      setFollowUpQuestions([]);
      setConversationMessages([]);
      setCurrentStreamingMessage("");

      const abortController = getAbortController();
      const signal = abortController.signal;

      // Navigate immediately to response page
      navigate("/response");

      // Fetch clinical case data in parallel (now streaming)
      const processClinicalCase = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/process_clinical_case_realtime`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                clinical_question: inputValue,
                clinical_notes: selectedNotes,
              }),
              signal,
            },
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          // let llmAccumulated = ''; // TODO: Use this when showing progressive ICD-10 rationale

          if (reader) {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                break;
              }

              if (value) {
                buffer += decoder.decode(value, { stream: true });

                // Process any complete SSE messages in the buffer
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || ""; // Keep any incomplete chunk in buffer

                for (const part of parts) {
                  const lines = part.split("\n");
                  for (const line of lines) {
                    if (line.startsWith("data:")) {
                      const jsonStr = line.replace("data:", "").trim();
                      try {
                        const parsed = JSON.parse(jsonStr);

                        // Handle different message types
                        if (parsed.type === "start") {
                          console.log(
                            "Clinical case processing started:",
                            parsed.chunk,
                          );
                        } else if (parsed.type === "status") {
                          console.log("Status:", parsed.chunk);
                        } else if (
                          parsed.type === "patient_info" &&
                          parsed.data
                        ) {
                          // Update clinical case data with patient info
                          setClinicalCaseData((prev) => ({
                            ...(prev || {}),
                            patient_age: parsed.data.age,
                            patient_gender: parsed.data.gender,
                          }));
                        } else if (parsed.type === "llm") {
                          // LLM streaming chunks (for ICD-10 matching)
                          // TODO: Accumulate and display progressive ICD-10 rationale
                          // llmAccumulated += parsed.chunk;
                          // console.log('LLM streaming:', parsed.chunk);
                        } else if (parsed.type === "result" && parsed.data) {
                          // Final result with complete data
                          setClinicalCaseData(parsed.data);
                          console.log("Clinical case result:", parsed.data);
                        } else if (parsed.type === "done") {
                          console.log("Clinical case processing completed");
                        }
                      } catch (err) {
                        console.warn(
                          "Invalid JSON in clinical case data:",
                          err,
                        );
                      }
                    }
                  }
                }
              }
            }
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log("Clinical case streaming aborted");
            return;
          }
          console.error("Error fetching clinical case:", error);
          setClinicalCaseData({ error: "Failed to process clinical case" });
        }
      };

      processClinicalCase();

      // Fetch specialist response in parallel (new separate API)
      const processSpecialistStream = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/ask-special-stream`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                question: inputValue,
                clinicalNotes: selectedNotes,
              }),
              signal,
            },
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let specialistAccumulatedText = "";

          if (reader) {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                break;
              }

              if (value) {
                buffer += decoder.decode(value, { stream: true });

                // Process any complete SSE messages in the buffer
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || ""; // Keep any incomplete chunk in buffer

                for (const part of parts) {
                  const lines = part.split("\n");
                  for (const line of lines) {
                    if (line.startsWith("data:")) {
                      const jsonStr = line.replace("data:", "").trim();
                      try {
                        const parsed = JSON.parse(jsonStr);

                        // Handle specialist message types
                        if (parsed.type === "specialist_start") {
                          console.log("Specialist stream started");
                          specialistAccumulatedText = "";
                        } else if (
                          parsed.type === "specialist" &&
                          parsed.chunk
                        ) {
                          // Accumulate specialist markdown chunks
                          specialistAccumulatedText += parsed.chunk;

                          // Update streamed data progressively
                          setStreamedData(specialistAccumulatedText);

                          // Also update parsed data to maintain compatibility
                          setParsedData((prev) => ({
                            ...(prev || {}),
                            specialistAIResponse: {
                              ...(prev?.specialistAIResponse || {}),
                              summaryResponse: specialistAccumulatedText,
                            },
                          }));
                        } else if (
                          parsed.type === "specialist_parsed" &&
                          parsed.data
                        ) {
                          // Final specialist response received
                          const finalResponse = parsed.data.summaryResponse;
                          setStreamedData(finalResponse);
                          setParsedData((prev) => ({
                            ...(prev || {}),
                            specialistAIResponse: {
                              ...(prev?.specialistAIResponse || {}),
                              summaryResponse: finalResponse,
                            },
                          }));
                          console.log("Specialist response completed");
                        } else if (parsed.type === "specialist_done") {
                          console.log("Specialist stream finished");
                        } else if (parsed.type === "done") {
                          console.log("Specialist streaming done");
                        }
                      } catch (err) {
                        console.warn("Invalid JSON in specialist data:", err);
                      }
                    }
                  }
                }
              }
            }
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log("Specialist streaming aborted");
            return;
          }
          console.error("Error fetching specialist response:", error);
          setStreamedData(
            "An error occurred while processing specialist response. Please try again.",
          );
        }
      };

      processSpecialistStream();

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/referral-streamed-realtime`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              question: inputValue,
              clinicalNotes: selectedNotes,
            }),
            signal,
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        if (reader) {
          let accumulatedText = "";

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              setIsStreaming(false);

              // Fetch follow-up questions after streaming completes
              setIsLoadingFollowUp(true);
              try {
                const followUpResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/followup-questions`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    credentials: "include",
                    signal,
                  },
                );

                if (followUpResponse.ok) {
                  const followUpData = await followUpResponse.json();

                  if (Array.isArray(followUpData)) {
                    setFollowUpQuestions(followUpData);
                  } else if (
                    followUpData.questions &&
                    Array.isArray(followUpData.questions)
                  ) {
                    setFollowUpQuestions(followUpData.questions);
                  }
                } else {
                  console.error(
                    "Follow-up questions API error:",
                    followUpResponse.status,
                  );
                }
              } catch (error) {
                console.error("Error fetching follow-up questions:", error);
              } finally {
                setIsLoadingFollowUp(false);
              }

              break;
            }

            if (value) {
              buffer += decoder.decode(value, { stream: true });

              // Process any complete SSE messages in the buffer
              const parts = buffer.split("\n\n");
              buffer = parts.pop() || ""; // Keep any incomplete chunk in buffer

              for (const part of parts) {
                const lines = part.split("\n");
                for (const line of lines) {
                  if (line.startsWith("data:")) {
                    const jsonStr = line.replace("data:", "").trim();
                    try {
                      const parsed = JSON.parse(jsonStr);

                      // Handle all message types from the new streaming API
                      if (parsed.type === "status") {
                        console.log("Status:", parsed.chunk);
                      } else if (parsed.type === "template" && parsed.data) {
                        // Template data received - store it in parsed data
                        setParsedData((prev) => ({
                          ...(prev || {}),
                          templateInfo: parsed.data,
                        }));
                        console.log("Template received:", parsed.data);
                      } else if (parsed.type === "streaming_started") {
                        console.log("Streaming started");
                      } else if (parsed.type === "llm_start") {
                        console.log("LLM stream started");
                      } else if (parsed.type === "llm") {
                        // LLM chunks building JSON - could accumulate for progressive display
                        // For now, we'll wait for llm_parsed
                      } else if (parsed.type === "llm_parsed" && parsed.data) {
                        // Final structured data received (patient summary, template, etc.)
                        setParsedData((prev) => ({
                          ...(prev || {}),
                          specialistSummary: parsed.data.specialistSummary,
                          basicPatientSummary:
                            parsed.data.basicPatientSummary?.map(
                              (value: string, index: number) => {
                                const fields = [
                                  "Age",
                                  "Gender",
                                  "Conditions",
                                  "Medications",
                                ];
                                return {
                                  field: fields[index] || `Field ${index + 1}`,
                                  value: value,
                                };
                              },
                            ),
                          populatedTemplate: parsed.data.populatedTemplate,
                        }));
                        console.log("LLM parsed data received:", parsed.data);
                      } else if (parsed.type === "llm_done") {
                        console.log("LLM stream finished");
                      } else if (parsed.type === "done") {
                        console.log("Streaming completed");
                      } else if (parsed.type === "start") {
                        // Simple start message (old format)
                        accumulatedText = "";
                        console.log("Streaming started");
                      } else if (parsed.type === "text" && parsed.chunk) {
                        // Simple text chunks (old format - for backward compatibility)
                        accumulatedText += parsed.chunk;
                        setStreamedData(accumulatedText);
                        setParsedData((prev) => ({
                          ...(prev || {}),
                          specialistAIResponse: {
                            ...(prev?.specialistAIResponse || {}),
                            summaryResponse: accumulatedText,
                          },
                        }));
                      } else {
                        // Fallback: handle old format for backward compatibility
                        setParsedData((prev) => {
                          const merged = {
                            ...(prev || {}),
                            ...parsed,
                          };
                          return merged;
                        });

                        // Update streamed data with the AI response (old format)
                        if (parsed.specialistAIResponse?.summaryResponse) {
                          setStreamedData(
                            parsed.specialistAIResponse.summaryResponse,
                          );
                        }
                      }
                    } catch (err) {
                      console.warn("Invalid JSON in data:", err);
                    }
                  }
                }
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log("Referral streaming aborted");
          return;
        }
        console.error("Error submitting data:", error);
        setStreamedData(
          "An error occurred while processing your request. Please try again.",
        );
        setIsStreaming(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleQuestionClick = (questionText: string, notes: string) => {
    setInputValue(questionText);
    setSelectedNotes(notes);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="index-container">
      <header className="header-text-containers">
        <div className="brand">SAGE</div>
      </header>

      <div className="content">
        <h1 className="main-heading">How can I help?</h1>

        <p className="subheading-primary">
          Your AI Specialist for Everyday Clinical Decisions
        </p>
        <p className="subheading-secondary">
          Specialist support for primary care physicians,
          <br />
          powered by evidence-based, medical AI
        </p>

        <div className="input-section">
          <div className="input-wrapper">
            <textarea
              className="clinical-input"
              placeholder="Choose a clinical question to get started"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              rows={1}
              readOnly={true}
            />
            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
            >
              {isLoading ? (
                "..."
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              )}
            </button>
          </div>

          <div className="example-questions">
            {questionsData.questions.map((data) => (
              <div
                key={data.id}
                className="example-question"
                onClick={() => handleQuestionClick(data.question, data.notes)}
                data-tooltip={data.question}
              >
                <span className="question-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </span>
                <span className="question-text">{data.question}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="about-sage-section">
          <div className="divider-container">
            <div className="line"></div>
            <span className="divider-text">About SAGE</span>
            <div className="line"></div>
          </div>

          <p className="brand-description">
            SAGE is an AI clinical tool in pilot stage, designed to integrate
            with platforms like Epic for quick, evidence-based insights.
          </p>

          <div className="partners-section">
            <div className="partner-item">
              <img
                src="/standford%20medicine%20logo.png"
                alt="Stanford Medicine"
                className="partner-logo-img"
              />
              <span className="partner-caption">
                In collaboration with Stanford Medicine
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* <footer className="footer">
        <p className="disclaimer">
          SAGE is a research product, is not medical care, and should be used
          with a healthcare professional.
          <br />
          Copyright © {new Date().getFullYear()} - All rights reserved by ARISE.
        </p>
      </footer> */}
    </div>
  );
};

export default Index;
